import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { WORKFLOW_SEQUENCE, PROJECTS, EVIDENCE_STATUSES } from "../config/wiseboardConfig";
import { ShieldCheck, ShieldAlert, Shield, AlertTriangle, CircleAlert } from "lucide-react";

type TrafficLight = "green" | "amber" | "red";

interface ProjectCompliance {
  projectId: string;
  light: TrafficLight;
  issues: string[];
  evidenceStats: { found: number; missing: number; stale: number; assumptions: number; total: number };
}

const ALWAYS_ON_RULES = [
  { id: "no_assumption_promoted", label: "No ASSUMPTION promoted to board recommendation without naming it", check: (c: ProjectCompliance) => c.evidenceStats.assumptions > 0 ? "Assumptions present — must be named explicitly" : null },
  { id: "stale_flagged", label: "All POTENTIALLY STALE items flagged", check: (c: ProjectCompliance) => c.evidenceStats.stale > 0 ? `${c.evidenceStats.stale} stale item(s) require freshness review` : null },
  { id: "not_found_visible", label: "NOT FOUND items visible, not hidden", check: (c: ProjectCompliance) => c.evidenceStats.missing > 0 ? `${c.evidenceStats.missing} item(s) still NOT FOUND` : null },
];

interface Props {
  caseId: string;
  refreshKey?: number;
}

export default function ComplianceTrafficLight({ caseId, refreshKey }: Props) {
  const [compliance, setCompliance] = useState<ProjectCompliance[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!caseId) return;
    const load = async () => {
      const [evidenceRes, artifactRes, projectRes] = await Promise.all([
        supabase.from("wiseboard_evidence_matrix").select("hypothesis_id, status, updated_by_project, confidence").eq("case_id", caseId),
        supabase.from("wiseboard_artifacts").select("project_id, approval_status, confidence, version").eq("approval_status", "approved"),
        supabase.from("wiseboard_projects").select("id, project_type, status").eq("case_id", caseId),
      ]);

      const evidenceRows = evidenceRes.data || [];
      const artifacts = artifactRes.data || [];
      const projects = projectRes.data || [];

      const projectIdMap: Record<string, string> = {};
      projects.forEach((p: any) => { projectIdMap[p.project_type] = p.id; });

      const results: ProjectCompliance[] = WORKFLOW_SEQUENCE.map(pid => {
        const dbProjectId = projectIdMap[pid];
        const projectStatus = projects.find((p: any) => p.project_type === pid)?.status || "not_started";

        // Evidence linked to this project
        const laneEvidence = evidenceRows.filter((e: any) => e.updated_by_project === pid);
        const found = laneEvidence.filter((e: any) => e.status === "DATA_FOUND").length;
        const missing = laneEvidence.filter((e: any) => e.status === "NOT_FOUND").length;
        const stale = laneEvidence.filter((e: any) => e.status === "POTENTIALLY_STALE").length;
        const assumptions = laneEvidence.filter((e: any) => e.status === "ASSUMPTION").length;
        const singleSource = laneEvidence.filter((e: any) => e.status === "WEB_SOURCED_SINGLE_SOURCE").length;

        const issues: string[] = [];

        // Approved artifact with low confidence
        const projArtifacts = dbProjectId ? artifacts.filter((a: any) => a.project_id === dbProjectId) : [];
        const lowConfidence = projArtifacts.some((a: any) => a.confidence !== null && a.confidence < 50);
        if (lowConfidence) issues.push("Approved artifact has confidence < 50%");

        // Evidence quality checks
        if (assumptions > 0) issues.push(`${assumptions} assumption(s) in evidence`);
        if (stale > 0) issues.push(`${stale} potentially stale item(s)`);
        if (singleSource > 0) issues.push(`${singleSource} single-source item(s)`);
        if (missing > 0 && projectStatus === "approved") issues.push(`Approved with ${missing} NOT FOUND item(s)`);

        // Classify traffic light
        let light: TrafficLight = "green";
        if (projectStatus === "not_started") {
          light = "green"; // not yet relevant
        } else if (
          (missing > 0 && projectStatus === "approved") ||
          lowConfidence ||
          assumptions >= 3
        ) {
          light = "red";
        } else if (
          assumptions > 0 ||
          stale > 0 ||
          singleSource > 0
        ) {
          light = "amber";
        }

        return {
          projectId: pid,
          light,
          issues,
          evidenceStats: { found, missing, stale, assumptions, total: laneEvidence.length },
        };
      });

      setCompliance(results);
      setLoading(false);
    };
    load();
  }, [caseId, refreshKey]);

  if (loading) return <div className="text-sm text-muted-foreground">Loading compliance...</div>;

  const redCount = compliance.filter(c => c.light === "red").length;
  const amberCount = compliance.filter(c => c.light === "amber").length;
  const greenCount = compliance.filter(c => c.light === "green").length;

  // Always-on rule violations across all projects
  const globalViolations: { rule: string; projects: string[] }[] = [];
  ALWAYS_ON_RULES.forEach(rule => {
    const violators = compliance.filter(c => {
      const msg = rule.check(c);
      return msg !== null;
    }).map(c => c.projectId);
    if (violators.length > 0) {
      globalViolations.push({ rule: rule.label, projects: violators });
    }
  });

  const lightIcon = (light: TrafficLight) => {
    switch (light) {
      case "green": return <ShieldCheck className="h-4 w-4 text-green-600" />;
      case "amber": return <ShieldAlert className="h-4 w-4 text-yellow-600" />;
      case "red": return <Shield className="h-4 w-4 text-red-600" />;
    }
  };

  const lightBg = (light: TrafficLight) => {
    switch (light) {
      case "green": return "bg-green-100 border-green-300 dark:bg-green-950/30 dark:border-green-800";
      case "amber": return "bg-yellow-50 border-yellow-300 dark:bg-yellow-950/30 dark:border-yellow-800";
      case "red": return "bg-red-50 border-red-300 dark:bg-red-950/30 dark:border-red-800";
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Compliance Traffic Light</CardTitle>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Badge className="bg-green-100 text-green-800 text-[10px]">{greenCount} Green</Badge>
            <Badge className="bg-yellow-100 text-yellow-800 text-[10px]">{amberCount} Amber</Badge>
            <Badge className="bg-red-100 text-red-800 text-[10px]">{redCount} Red</Badge>
          </div>
        </div>
        <CardDescription className="text-xs">
          Per-project data classification with escalation warnings and always-on evidence rules
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Always-on rule violations */}
        {globalViolations.length > 0 && (
          <div className="rounded-lg border border-red-200 bg-red-50/50 dark:bg-red-950/20 dark:border-red-900 p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-red-700 dark:text-red-400">
              <CircleAlert className="h-4 w-4" />
              Always-On Rule Violations
            </div>
            {globalViolations.map((v, i) => (
              <div key={i} className="text-xs">
                <span className="font-medium">{v.rule}</span>
                <span className="text-muted-foreground ml-2">
                  — {v.projects.join(", ")}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Per-project grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {compliance.map(c => {
            const project = PROJECTS[c.projectId];
            if (!project) return null;
            return (
              <div
                key={c.projectId}
                className={`rounded-lg border p-2 text-center transition-all ${lightBg(c.light)}`}
              >
                <div className="flex items-center justify-center gap-1 mb-1">
                  {lightIcon(c.light)}
                  <span className="text-xs font-bold">{c.projectId.replace("_", " ")}</span>
                </div>
                <p className="text-[10px] text-muted-foreground truncate">{project.name}</p>
                {c.issues.length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {c.issues.slice(0, 2).map((issue, i) => (
                      <div key={i} className="flex items-start gap-1">
                        <AlertTriangle className="h-2.5 w-2.5 text-yellow-600 shrink-0 mt-0.5" />
                        <span className="text-[9px] text-muted-foreground text-left leading-tight">{issue}</span>
                      </div>
                    ))}
                    {c.issues.length > 2 && (
                      <span className="text-[9px] text-muted-foreground">+{c.issues.length - 2} more</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
